import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, eur, openPdf } from "@/pages/mrv/mrvApi";
import OutcomeBadge from "@/pages/rc/OutcomeBadge";
import MRVLayout from "@/pages/mrv/MRVLayout";
import ClienteCollegati from "@/components/ClienteCollegati";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Send, Cloud, Copy, TerminalSquare, Pencil, Sparkles, Save, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const GARANZIA_LABELS = {
  rc: "RC verso Terzi", morte: "Infortuni - Caso Morte", ip: "Infortuni - Invalidità Permanente",
  ricovero: "Indennità da Ricovero", immob: "Indennità da Immobilizzazione", rsm: "Rimborso Spese Mediche",
};
const fmtDate = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "—");

export default function MRVQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [audit, setAudit] = useState(null);
  const [email, setEmail] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/quotes/${id}`).then((r) => { setQuote(r.data); setEmail(r.data.organization?.email || ""); });
    api.get(`/quotes/${id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!quote) return <MRVLayout><div className="text-stone-400 font-mono text-sm">Caricamento…</div></MRVLayout>;
  const prem = quote.premium_result;
  const inp = quote.input_payload || {};
  const opz = (inp.opzione_tipo || "A").toUpperCase();

  const doPdf = async () => {
    try { await openPdf(`/quotes/${id}/pdf`, `preventivo_${quote.quote_number}.pdf`); toast.success("PDF generato e scaricato."); }
    catch (e) { toast.error("PDF non disponibile"); }
  };
  const doEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/email`, { recipient: email, message: emailMsg });
      toast.success(data.note || "Email inviata.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doCrm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/crm-sync`);
      toast.success(`${data.note} (ID: ${data.external_id})`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doDuplicate = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      toast.success("Preventivo duplicato.");
      navigate(`/multirischi-volontariato/preventivi/${data.id}`);
    } catch (e) { toast.error("Errore duplicazione"); }
  };
  const doSetPremio = async (scelta) => {
    try {
      await api.patch(`/quotes/${id}/premio`, { premio_scelta: scelta });
      toast.success(`Premio impostato: ${scelta === "minimo" ? "minimo di polizza" : "da tariffa"}.`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doSetEffetto = async (dataEffetto) => {
    try {
      await api.patch(`/quotes/${id}/premio`, { data_effetto: dataEffetto });
      toast.success("Data di effetto aggiornata, rateo ricalcolato.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <MRVLayout>
      <div data-testid="mrv-quote-detail-page">
        <button onClick={() => navigate("/multirischi-volontariato/preventivi")} className="text-xs text-stone-500 hover:text-stone-900 mb-3">← Storico</button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-900 font-mono">{quote.quote_number}</h1>
              <OutcomeBadge status={quote.calc_status} size="lg" />
            </div>
            <p className="text-stone-500 text-sm mt-1">
              {quote.organization?.ragione_sociale || "Ente non collegato"} · Versione {quote.product_version} · Operatore {quote.operator_name}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <Button data-testid="mrv-edit-btn" onClick={() => navigate(`/multirischi-volontariato/preventivi/${id}/modifica`)} className="rounded-sm bg-stone-900 hover:bg-stone-800">
            <Pencil className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Modifica
          </Button>
          <Button data-testid="mrv-pdf-btn" onClick={doPdf} variant="outline" className="rounded-sm">
            <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Genera PDF
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button data-testid="mrv-email-btn" variant="outline" className="rounded-sm">
                <Send className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Invia Email
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invia preventivo via email</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Destinatario</Label>
                  <Input data-testid="mrv-email-recipient" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Messaggio</Label>
                  <Textarea data-testid="mrv-email-message" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} className="mt-1.5 rounded-sm" rows={4}
                    placeholder="Gentile cliente, in allegato il preventivo richiesto…" />
                </div>
                <p className="text-xs text-amber-700">Invio simulato (mock). Configurare un provider reale per l'invio effettivo.</p>
              </div>
              <DialogFooter>
                <Button data-testid="mrv-email-send-confirm" onClick={doEmail} disabled={busy} className="rounded-sm bg-stone-900 hover:bg-stone-800">Invia</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button data-testid="mrv-crm-btn" onClick={doCrm} disabled={busy} variant="outline" className="rounded-sm">
            <Cloud className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Invia a CRM
          </Button>
          <Button data-testid="mrv-duplicate-btn" onClick={doDuplicate} variant="outline" className="rounded-sm">
            <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Duplica
          </Button>
        </div>

        <div className="mb-6">
          <ClienteCollegati currentModule="mrv" currentId={id}
            anagraficaId={quote.organization_id} nome={quote.organization?.ragione_sociale} piva={quote.organization?.partita_iva} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-5">
              <h2 className="font-display text-lg font-medium mb-4">Riepilogo configurazione</h2>
              <dl className="grid grid-cols-2 gap-y-3 text-sm">
                <Row label="Opzione" value={`${opz} — ${opz === "A" ? "RC + Infortuni + Malattia" : "solo Infortuni + Malattia"}`} />
                <Row label="Combinazione" value={inp.combinazione} />
                <Row label="Numero volontari" value={inp.n_volontari} />
                <Row label="Operatore" value={quote.operator_name} />
              </dl>
            </div>

            {prem && (
              <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-stone-200"><h2 className="font-display text-lg font-medium">Dettaglio premio (da tariffa)</h2></div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-100 text-stone-500 text-xs uppercase tracking-wide">
                      <th className="text-left font-medium px-5 py-2">Garanzia</th>
                      <th className="text-right font-medium px-5 py-2">Imponibile</th>
                      <th className="text-right font-medium px-5 py-2">Imposte</th>
                      <th className="text-right font-medium px-5 py-2">Lordo</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {Object.entries(prem.breakdown).map(([k, v]) => (
                      <tr key={k} className="border-b border-stone-100">
                        <td className="px-5 py-2.5 font-sans text-stone-700">{GARANZIA_LABELS[k] || k}</td>
                        <td className="px-5 py-2.5 text-right">{eur(v.net)}</td>
                        <td className="px-5 py-2.5 text-right text-stone-400">{eur(v.tax)}</td>
                        <td className="px-5 py-2.5 text-right">{eur(v.gross)}</td>
                      </tr>
                    ))}
                    <tr className="bg-stone-50 font-semibold">
                      <td className="px-5 py-2.5 font-sans">PREMIO ANNUO DA TARIFFA</td>
                      <td className="px-5 py-2.5 text-right">{eur(prem.tariff_net_total)}</td>
                      <td className="px-5 py-2.5 text-right text-stone-400">{eur(prem.tariff_tax_total)}</td>
                      <td className="px-5 py-2.5 text-right" data-testid="mrv-detail-tariff-total">{eur(prem.tariff_gross_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {prem && <PremioCard prem={prem} inp={inp} onSetPremio={doSetPremio} onSetEffetto={doSetEffetto} />}

            {(quote.eligibility_result?.warnings?.length > 0 || quote.eligibility_result?.blocking_errors?.length > 0) && (
              <div className="space-y-2">
                {quote.eligibility_result.blocking_errors.map((e, i) => (
                  <p key={`blk-${i}`} className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2">{e}</p>
                ))}
                {quote.eligibility_result.warnings.map((e, i) => (
                  <p key={`wrn-${i}`} className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">{e}</p>
                ))}
              </div>
            )}

            <RelazioneCard quoteId={id} quoteNumber={quote.quote_number} />
          </div>

          <div>
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-4 sticky top-8" data-testid="mrv-audit-box">
              <div className="flex items-center gap-2 mb-3 text-stone-500">
                <TerminalSquare className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-widest">Audit del calcolo</span>
              </div>
              {!audit && <p className="text-xs text-stone-400 audit-console">Audit non disponibile (bozza).</p>}
              {audit && (
                <div className="audit-console text-stone-700 space-y-2">
                  <div className="text-stone-400">› versione {audit.product_version}</div>
                  {(audit.all_steps || []).map((r, i) => (
                    <div key={`${r.rule}-${i}`} className="border-l-2 border-stone-300 pl-2">
                      <div className="text-stone-500">[{r.rule}] {r.label || r.msg}</div>
                      {r.formula && <div className="text-stone-800">{r.formula}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MRVLayout>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-stone-400">{label}</dt>
      <dd className="text-stone-800 mt-0.5">{value}</dd>
    </div>
  );
}

function PremioCard({ prem, inp, onSetPremio, onSetEffetto }) {
  const [effetto, setEffetto] = useState((prem.rateo && prem.rateo.data_effetto) || inp.data_effetto || "");
  const scelta = prem.premio_scelta || "tariffa";
  const rateo = prem.rateo || {};
  const applicable = prem.minimum_premium_applicable;

  return (
    <div data-testid="mrv-premio-card" className="bg-white border border-stone-200 rounded-sm shadow-sm p-5">
      <h2 className="font-display text-lg font-medium mb-4">Premio da consegnare & rateo</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className={`rounded-sm border p-3 ${scelta === "tariffa" ? "border-teal-600 bg-teal-50" : "border-stone-200"}`}>
          <div className="text-xs uppercase tracking-widest text-stone-500">Premio annuo da tariffa</div>
          <div className="font-display text-2xl font-semibold text-stone-900 mt-1" data-testid="mrv-premio-tariffa">{eur(prem.tariff_gross_total)}</div>
        </div>
        <div className={`rounded-sm border p-3 ${!applicable ? "opacity-40" : scelta === "minimo" ? "border-teal-600 bg-teal-50" : "border-stone-200"}`}>
          <div className="text-xs uppercase tracking-widest text-stone-500">Premio minimo di polizza</div>
          <div className="font-display text-2xl font-semibold text-stone-900 mt-1" data-testid="mrv-premio-minimo">
            {applicable ? eur(prem.minimum_premium_value) : "—"}
          </div>
        </div>
      </div>

      {applicable && (
        <div className="mb-4">
          <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Premio da applicare/consegnare</Label>
          <div className="inline-flex rounded-sm border border-stone-200 overflow-hidden">
            <button data-testid="mrv-scelta-tariffa" onClick={() => onSetPremio("tariffa")}
              className={`px-4 py-2 text-sm ${scelta === "tariffa" ? "bg-teal-600 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}>Da tariffa</button>
            <button data-testid="mrv-scelta-minimo" onClick={() => onSetPremio("minimo")}
              className={`px-4 py-2 text-sm border-l border-stone-200 ${scelta === "minimo" ? "bg-teal-600 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}>Minimo di polizza</button>
          </div>
          <p className="text-xs text-amber-700 mt-2">Il premio da tariffa è inferiore al minimo di polizza: scegli tu quale consegnare al cliente.</p>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-stone-200 pt-3 mb-4">
        <span className="text-xs uppercase tracking-widest text-teal-700">Premio annuo applicato ({scelta === "minimo" ? "minimo" : "da tariffa"})</span>
        <span className="font-display text-2xl font-semibold text-teal-800" data-testid="mrv-premio-applicato">{eur(prem.gross_total)}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 mb-1.5 block">Data di effetto</Label>
          <div className="flex gap-2">
            <Input data-testid="mrv-detail-effetto" type="date" value={effetto} onChange={(e) => setEffetto(e.target.value)} className="rounded-sm" />
            <Button data-testid="mrv-detail-effetto-apply" variant="outline" className="rounded-sm" onClick={() => onSetEffetto(effetto)}>Applica</Button>
          </div>
        </div>
        <div className="rounded-sm bg-stone-50 border border-stone-200 p-3" data-testid="mrv-rateo-box">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-stone-500">Rateo ({rateo.giorni} gg / 365)</span>
            <span className="font-mono font-semibold text-stone-900" data-testid="mrv-rateo-importo">{eur(rateo.importo)}</span>
          </div>
          <div className="flex items-center justify-between mt-1 text-xs text-stone-400">
            <span>Dal {fmtDate(rateo.data_effetto)} al {fmtDate(rateo.scadenza_rateo)}</span>
            <span>Scad. annuale {fmtDate(rateo.scadenza_annuale)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RelazioneCard({ quoteId, quoteNumber }) {
  const [sito, setSito] = useState("");
  const [framework, setFramework] = useState("");
  const [testo, setTesto] = useState("");
  const [showFramework, setShowFramework] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/quotes/${quoteId}/relazione`).then((r) => {
      setSito(r.data.sito_web || "");
      setFramework(r.data.framework || "");
      setTesto(r.data.relazione_testo || "");
    }).catch(() => {});
  }, [quoteId]);

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/relazione/generate`, { sito_web: sito, framework });
      setTesto(data.relazione_testo || "");
      toast.success("Relazione generata con l'AI.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setGenerating(false); }
  };
  const doSave = async () => {
    setSaving(true);
    try {
      await api.put(`/quotes/${quoteId}/relazione`, { relazione_testo: testo, sito_web: sito, framework });
      toast.success("Relazione salvata.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const doPdf = async () => {
    try { await openPdf(`/quotes/${quoteId}/relazione/pdf`, `relazione_${quoteNumber}.pdf`); }
    catch (e) { toast.error("Salva o genera la relazione prima di esportare il PDF."); }
  };

  return (
    <div data-testid="mrv-relazione-card" className="bg-white border border-stone-200 rounded-sm shadow-sm p-5">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-stone-700" strokeWidth={1.5} />
        <h2 className="font-display text-lg font-medium">Relazione tecnica (AI)</h2>
      </div>
      <p className="text-xs text-stone-500 mb-4">
        Inserisci il sito del cliente: l'AI raccoglie le informazioni e compila la relazione seguendo la traccia. Puoi modificare la traccia e il testo prima di salvare.
      </p>

      <div className="space-y-1.5 mb-3">
        <Label className="text-xs uppercase tracking-widest text-stone-500">Sito web del cliente</Label>
        <Input data-testid="mrv-relazione-sito" value={sito} onChange={(e) => setSito(e.target.value)}
          placeholder="https://www.esempio.it" className="rounded-sm" />
      </div>

      <button data-testid="mrv-relazione-toggle-framework" onClick={() => setShowFramework((v) => !v)}
        className="text-xs text-stone-500 hover:text-stone-900 mb-2">
        {showFramework ? "▾ Nascondi traccia (framework)" : "▸ Personalizza traccia (framework)"}
      </button>
      {showFramework && (
        <Textarea data-testid="mrv-relazione-framework" value={framework} onChange={(e) => setFramework(e.target.value)}
          rows={10} className="rounded-sm font-mono text-xs mb-3" />
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <Button data-testid="mrv-relazione-generate-btn" onClick={doGenerate} disabled={generating}
          className="rounded-sm bg-stone-900 hover:bg-stone-800">
          {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.5} />}
          {generating ? "Generazione…" : (testo ? "Rigenera con AI" : "Genera con AI")}
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-widest text-stone-500">Testo relazione</Label>
        <Textarea data-testid="mrv-relazione-testo" value={testo} onChange={(e) => setTesto(e.target.value)}
          rows={16} placeholder="La relazione generata comparirà qui e potrà essere modificata prima di salvare."
          className="rounded-sm text-sm leading-relaxed" />
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <Button data-testid="mrv-relazione-save-btn" onClick={doSave} disabled={saving || !testo} variant="outline" className="rounded-sm">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" strokeWidth={1.5} />}
          Salva
        </Button>
        <Button data-testid="mrv-relazione-pdf-btn" onClick={doPdf} disabled={!testo} variant="outline" className="rounded-sm">
          <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Scarica PDF
        </Button>
      </div>
    </div>
  );
}
